/**
 * LocaleProvider — React context wrapping the i18next instance.
 * docs/NORMAX_PERFORMANCE_I18N_PHASE0_IMPLEMENTATION_PLAN.md §6.
 *
 * Deliberately independent of AuthProvider/tenant state (architecture plan
 * §6.4, §7) — language choice is a per-browser preference, not tied to the
 * signed-in user or company.
 */
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import i18next, { initialLocale } from "./index";
import {
  INTL_LOCALE_MAP,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  SupportedLocale,
} from "./locale";

export interface LanguageOption {
  value: SupportedLocale;
  /** i18next key (in the "common" namespace) for this language's display name. */
  labelKey: `languages.${SupportedLocale}`;
}

export interface LocaleContextValue {
  locale: SupportedLocale;
  /** Intl.DateTimeFormat/NumberFormat locale string for the current locale, e.g. "sr-Latn-RS". */
  intlLocale: string;
  setLocale: (locale: SupportedLocale) => void;
  supportedLocales: readonly SupportedLocale[];
  languageOptions: readonly LanguageOption[];
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

const LANGUAGE_OPTIONS: readonly LanguageOption[] = SUPPORTED_LOCALES.map((value) => ({
  value,
  labelKey: `languages.${value}`,
}));

/** Defensive localStorage write — mirrors draft-storage.ts; a private-mode/
 * quota exception must never break the language switch itself. */
function persistLocale(locale: SupportedLocale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // best-effort, same as draft-storage.ts
  }
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  // initialLocale is the SAME value already passed to i18next.init()'s `lng`
  // option (./index.ts) — reusing it here (rather than re-resolving) keeps
  // React state and the i18next instance in sync from the very first render.
  const [locale, setLocaleState] = useState<SupportedLocale>(initialLocale);

  useEffect(() => {
    // Covers the case where the user never explicitly changes the language
    // in this session (default/browser-detected locale) — the <html lang>
    // attribute still needs to be correct for a11y/screen readers.
    document.documentElement.lang = locale;
    // Intentionally runs once per `locale` change, including the initial
    // mount — setLocale() below also updates it directly, this effect is
    // the safety net for the un-interacted-with initial state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLocale = useCallback((next: SupportedLocale) => {
    void i18next.changeLanguage(next);
    setLocaleState(next);
    persistLocale(next);
    document.documentElement.lang = next;
  }, []);

  const intlLocale = INTL_LOCALE_MAP[locale];

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      intlLocale,
      setLocale,
      supportedLocales: SUPPORTED_LOCALES,
      languageOptions: LANGUAGE_OPTIONS,
    }),
    [locale, intlLocale, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}
